import React, { useCallback, PureComponent } from 'react';
import type { FC } from 'react';
import type { ExtendedKeyboardEvent, MousetrapStatic } from 'mousetrap';
import Mousetrap from 'mousetrap';
// importing `mousetrap-global-bind` for the side effect of adding bindGlobal to Mousetrap
// eslint-disable-next-line no-restricted-syntax
import 'mousetrap/plugins/global-bind/mousetrap-global-bind';

// @ts-ignore "@confluence/dialogs/entry-points/helpers" will be converted to "stricter" separately
import { isDialogShown } from '@confluence/dialogs/entry-points/helpers.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';

import { MOUSETRAP_ALLOWED_ELEMENTS, MOUSETRAP_ALLOWED_MODALS } from './constants.ts';
import { maybeShowDeprecatedShortcutFlag } from './DeprecatedShortcutFlag.tsx';

if (typeof window !== 'undefined') {
	// allow to track events on specific elements
	const originalStopCallback: MousetrapStatic['stopCallback'] = Mousetrap.prototype.stopCallback;
	// Note here should be function instead of arrow function as we want to preserve the context of this
	Mousetrap.prototype.stopCallback = function (
		...[e, element, combo]: Parameters<MousetrapStatic['stopCallback']>
	) {
		const isAllowed = MOUSETRAP_ALLOWED_ELEMENTS.map((className) =>
			element.classList.contains(className),
		).some((truthful) => truthful);

		const isAllowedInModal = MOUSETRAP_ALLOWED_MODALS.map((className) =>
			element.closest(`.${className}`),
		).some((truthful) => truthful);

		return isAllowed || isAllowedInModal
			? false
			: originalStopCallback.call(this, e, element, combo);
	};
}
const ALLOWED_MIN_TIME = 150;

export type ShortcutEvent = ExtendedKeyboardEvent;

type ShortcutConflict = {
	accelerator: string;
	conflictType: ShortcutType | undefined;
};

export enum ShortcutType {
	Confluence = 'Confluence',
	Forge = 'Forge',
}

type ShortcutListenerInternalProps = {
	/**
	 * A mousetrap accelerator
	 * Describes a keyboard combination
	 * E.g. ctrl+a
	 */
	accelerator: string | string[];
	/**
	 * If true, this keyboard shortcut can be activated while a dialog box is open.
	 */
	allowInDialog?: boolean;
	/**
	 * The function to call when the shortcut is triggered
	 */
	listener(e: ShortcutEvent, combo: string): void;
	/**
	 * Level of priority for overlapping shortcuts.
	 * Confluence keyboard shortcuts will take priority over custom Forge ones.
	 */
	shortcutType?: ShortcutType;
};
export type ShortcutListenerProps = {
	/**
	 * A mousetrap accelerator that describes an old, deprecated version of this shortcut
	 * This shortcut will still function properly
	 * Describes a keyboard combination
	 * E.g. ctrl+a
	 */
	deprecatedAccelerator?: string | string[];
} & ShortcutListenerInternalProps;

export class ShortcutListenerInternal extends PureComponent<ShortcutListenerInternalProps> {
	public static defaultProps: {
		allowInDialog: boolean;
		shortcutType: ShortcutType;
	} = {
		allowInDialog: false,
		shortcutType: ShortcutType.Confluence,
	};

	componentDidMount(): void {
		const { accelerator, shortcutType = ShortcutType.Confluence } = this.props;
		this.checkConflictThenBindAccelerator(accelerator, shortcutType);
	}

	componentDidUpdate(prevProps: Readonly<ShortcutListenerInternalProps>): void {
		const { accelerator: prevAccelerator } = prevProps;
		const { accelerator, shortcutType = ShortcutType.Confluence } = this.props;
		const prevAccelerators: string[] = this.getAccelerators(prevAccelerator);

		// Using JSON.stringify to compare because accelerator may be a list of strings
		if (JSON.stringify(prevAccelerator) !== JSON.stringify(accelerator)) {
			this.unbindAccelerators(prevAccelerators);
			this.checkConflictThenBindAccelerator(accelerator, shortcutType);
		}
	}

	componentWillUnmount(): void {
		const { accelerator } = this.props;
		const accelerators: string[] = this.getAccelerators(accelerator);

		this.unbindAccelerators(accelerators);
	}

	getAccelerators(accelerator: string | string[]): string[] {
		return Array.isArray(accelerator) ? accelerator : [accelerator];
	}

	getShortcutConflicts(accelerators: string[]): ShortcutConflict[] {
		return accelerators
			.map((a) => ({
				accelerator: a,
				conflictType: ShortcutListenerInternal.listeners[a],
			}))
			.filter((o) => o.conflictType);
	}

	isConfluenceShortcutAndAllConflictsAreForgeShortcuts(
		shortcutType: ShortcutType,
		shortcutConflicts: ShortcutConflict[],
	): boolean {
		return (
			shortcutType === ShortcutType.Confluence &&
			shortcutConflicts.every((o) => o.conflictType === ShortcutType.Forge)
		);
	}

	checkConflictThenBindAccelerator(
		accelerator: string | string[],
		shortcutType: ShortcutType,
	): void {
		const accelerators: string[] = this.getAccelerators(accelerator);

		const shortcutConflicts: ShortcutConflict[] = this.getShortcutConflicts(accelerators);
		if (!shortcutConflicts.length) {
			this.bindAccelerators(accelerators, shortcutType);
		} else if (
			this.isConfluenceShortcutAndAllConflictsAreForgeShortcuts(shortcutType, shortcutConflicts)
		) {
			shortcutConflicts.forEach((c) => Mousetrap.unbind(c.accelerator));
			this.bindAccelerators(accelerators, shortcutType);
		} else if (process.env.NODE_ENV !== 'production') {
			// eslint-disable-next-line no-console
			console.error(
				`Registered duplicate ShortcutListener: ${accelerator}. There should only be one component that binds this listener mounted at a time.`,
			);
		}
	}

	bindAccelerators(accelerators: string[], shortcutType: ShortcutType): void {
		accelerators.forEach((a) => {
			if (
				a.includes('+') &&
				shortcutType !== ShortcutType.Forge &&
				typeof Mousetrap.bindGlobal === 'function'
			) {
				// bindGlobal allows shortcut to work when a input field is focused, see https://github.com/ccampbell/mousetrap/blob/master/plugins/global-bind/README.md
				// This allows user to use shortcut to publish the page when focusing on editor title
				Mousetrap.bindGlobal(a, this._call);
			} else {
				Mousetrap.bind(a, this._call);
			}
			ShortcutListenerInternal.listeners[a] = shortcutType;
		});
	}

	unbindAccelerators(accelerators: string[]): void {
		accelerators.forEach((a) => {
			Mousetrap.unbind(a);
			delete ShortcutListenerInternal.listeners[a];
		});
	}

	public static listeners: Record<string, ShortcutType> = {};

	listeners = {};
	lastFired = 0;

	_call = (e: ShortcutEvent, combo: string): void => {
		// User can only fire a shortcut event in every 150ms
		// This can avoid a part of sequence being recognized as a single key shortcut
		// eg: "s" will be triggered when user enters "g s" without this
		const now = Date.now();
		if (now - this.lastFired >= ALLOWED_MIN_TIME) {
			if (this.props.allowInDialog || !isDialogShown()) {
				this.lastFired = now;
				this.props.listener(e, combo);
			}
		}
	};

	render() {
		return null;
	}
}

const ShortcutListenerFC: FC<ShortcutListenerProps & WithFlagsProps> = ({
	flags,
	deprecatedAccelerator,
	accelerator,
	listener,
	...otherProps
}) => {
	const deprecatedListener = useCallback(
		(e: KeyboardEvent, combo: string) => {
			maybeShowDeprecatedShortcutFlag({
				flags,
				acceleratorTriggered: combo,
				accelerator,
				deprecatedAccelerator: deprecatedAccelerator!, // Since this listener is only called when deprecatedAccelerator is provided, this type assertion is safe.
			});
			listener(e, combo);
		},
		[listener, accelerator, deprecatedAccelerator, flags],
	);

	return (
		<>
			{
				/* This 2nd ShortcutListenerInternal is used to also display a flag informing the user
			     that the shortcut they used is deprecated, while still triggering the listener. */
				deprecatedAccelerator && (
					<ShortcutListenerInternal
						accelerator={deprecatedAccelerator}
						listener={deprecatedListener}
						{...otherProps}
					/>
				)
			}
			<ShortcutListenerInternal accelerator={accelerator} listener={listener} {...otherProps} />
		</>
	);
};

export const ShortcutListener: React.ComponentType<
	Omit<
		{
			/**
			 * A mousetrap accelerator that describes an old, deprecated version of this shortcut
			 * This shortcut will still function properly
			 * Describes a keyboard combination
			 * E.g. ctrl+a
			 */
			deprecatedAccelerator?: string | string[];
		} & ShortcutListenerInternalProps,
		'flags'
	>
> = withFlags(ShortcutListenerFC);
